import React, { useEffect } from 'react';
import { 
  Box, 
  Button, 
  Container, 
  Typography, 
  Grid,
  Paper,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  useScrollTrigger,
  Slide,
  CircularProgress
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import SchoolIcon from '@mui/icons-material/School';
import LanguageIcon from '@mui/icons-material/Language';
import GroupIcon from '@mui/icons-material/Group';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router-dom';
import { isAdmin } from '../utils/authUtils.ts';

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
  100% { transform: translateY(0px); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const HeroSection = styled(Box)(({ theme }) => ({
  height: '40vh',
  minHeight: '300px',
  maxHeight: '400px',
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(4),
  padding: theme.spacing(3),
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '33.33%',
    height: '100%',
    background: '#002395',
    zIndex: 0
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    width: '33.33%',
    height: '100%',
    background: '#ED2939',
    zIndex: 0
  },
  '& .white-stripe': {
    position: 'absolute',
    top: 0,
    left: '33.33%',
    width: '33.34%',
    height: '100%',
    background: '#FFFFFF',
    zIndex: 0
  },
  '& .content': {
    position: 'relative',
    zIndex: 1,
    textAlign: 'center',
    color: '#444444',
    width: '100%'
  }
}));

const AnimatedBox = styled(Box)`
  animation: ${fadeIn} 1s ease-out;
`;

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  borderRadius: '15px',
  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 6px 25px rgba(0,0,0,0.15)',
  },
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(10px)',
}));

const FeatureCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  textAlign: 'center',
  height: '100%',
  borderRadius: '12px',
  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(10px)',
  '&:hover': {
    transform: 'translateY(-5px) scale(1.02)',
    boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
  },
  minWidth: '250px',
  maxWidth: '300px',
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: 'white',
  width: '50px',
  height: '50px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(2),
  transition: 'transform 0.3s ease-in-out',
  '&:hover': {
    transform: 'rotate(360deg)',
  },
}));

const ContactItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
  '& .MuiIcon-root': {
    marginRight: theme.spacing(2),
    color: theme.palette.primary.main,
  },
}));

interface Props {
  window?: () => Window;
}

const HomePage = (props: Props) => {
  const navigate = useNavigate();
  const trigger = useScrollTrigger({
    target: props.window ? props.window() : undefined,
  } as any);

  const auth = useAuth();

  useEffect(() => {
    if(auth.isAuthenticated && auth.user?.access_token && isAdmin(auth.user.access_token || "")) {
      navigate('/admin');
    } else if (auth.isAuthenticated) {
      navigate('/playlists');
    }
  }, [auth.isAuthenticated, navigate]);

  const signOutRedirect = () => {
    const clientId = "6ef72kj20grdu8bvk4nn5hn20t";
    const logoutUri = process.env.REACT_APP_COGNITO_LOGOUT_URI;
    const cognitoDomain = process.env.REACT_APP_COGNITO_DOMAIN;
    window.location.href = `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(logoutUri)}`;
  };

  if (auth.isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (auth.error) {
    return <div>Encountering error... {auth.error.message}</div>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="static" sx={{ backgroundColor: '#d9d9d9' }}>
        <Toolbar sx={{ justifyContent: 'flex-end' }}>
          <Box display="flex" gap={2}>
            <Button 
              variant="outlined" 
              sx={{ 
                borderColor: 'black', 
                color: 'black',
                '&:hover': {
                  borderColor: 'black',
                  backgroundColor: 'rgba(4, 17, 90, 0.1)'
                }
              }} 
              onClick={() => auth.signinRedirect()}
            >
              Login
            </Button>
            <Button 
              variant="outlined" 
              sx={{ 
                borderColor: 'white', 
                color: 'white',
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
              }} 
              onClick={() => auth.signinRedirect()}
            >
              Sign Up
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      <Box component="main" sx={{ flexGrow: 1 }}>
        <HeroSection>
          <div className="white-stripe" />
          <div className="content">
            <AnimatedBox>
              <Typography variant="h1" component="h1" align="center" sx={{ 
                fontSize: { xs: '2.5rem', md: '2.5rem' }, 
                mb: 1.5,
                color: '#444444',
                fontWeight: 900
              }}>
                The French Journey
              </Typography>
              <Typography variant="h4" align="center" sx={{ 
                maxWidth: '800px', 
                mx: 'auto', 
                px: 2,
                fontSize: { xs: '1rem', md: '1.2rem' },
                color: '#444444'
              }}>
                Embark on Your Journey to Master French Language and Culture
              </Typography>
            </AnimatedBox>
          </div>
        </HeroSection>

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box mb={6}>
            <Typography variant="h3" component="h2" align="center" gutterBottom color="primary">
              Why Choose Us?
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', md: 'row' },
              gap: 3,
              justifyContent: 'center',
              alignItems: 'center',
              mt: 2 
            }}>
              <FeatureCard>
                <CardContent>
                  <IconWrapper>
                    <SchoolIcon fontSize="large" />
                  </IconWrapper>
                  <Typography variant="h5" gutterBottom>
                    Expert Teachers
                  </Typography>
                  <Typography>
                    Learn from native French speakers
                  </Typography>
                </CardContent>
              </FeatureCard>

              <FeatureCard>
                <CardContent>
                  <IconWrapper>
                    <LanguageIcon fontSize="large" />
                  </IconWrapper>
                  <Typography variant="h5" gutterBottom>
                    Interactive Learning
                  </Typography>
                  <Typography>
                    Engage with interactive lessons
                  </Typography>
                </CardContent>
              </FeatureCard>

              <FeatureCard>
                <CardContent>
                  <IconWrapper>
                    <GroupIcon fontSize="large" />
                  </IconWrapper>
                  <Typography variant="h5" gutterBottom>
                    Community Support
                  </Typography>
                  <Typography>
                    Join our vibrant community
                  </Typography>
                </CardContent>
              </FeatureCard>
            </Box>
          </Box>

          <Box mb={6}>
            <StyledPaper>
              <Typography variant="h4" component="h2" gutterBottom color="primary" align="center">
                Contact Us
              </Typography>
              <Typography paragraph align="center" sx={{ mb: 4 }}>
                Have questions? We're here to help you on your French learning journey!
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', md: 'row' },
                gap: 4,
                justifyContent: 'center',
                alignItems: { xs: 'flex-start', md: 'center' }
              }}>
                <ContactItem>
                  <EmailIcon color="primary" sx={{ fontSize: 30 }} />
                  <Typography>contact@thefrenchjourney.com</Typography>
                </ContactItem>
                <ContactItem>
                  <PhoneIcon color="primary" sx={{ fontSize: 30 }} />
                  <Typography>+1 (555) 123-4567</Typography>
                </ContactItem>
                <ContactItem>
                  <LocationOnIcon color="primary" sx={{ fontSize: 30 }} />
                  <Typography>123 Language Street, Paris</Typography>
                </ContactItem>
              </Box>
            </StyledPaper>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default HomePage; 